import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageSquare, RefreshCw, Sparkles } from "lucide-react";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";
import { AGENT_VISUALS } from "@/lib/agents";


// Demo conversations (LIMOVA-style snippet for each agent)
const DEMOS = {
  gaby: {
    user: "Gaby, fais-moi un point général sur GABRYX ce matin.",
    agent: "Bonjour Gabriel ! Ta boîte en 3 chiffres : **3 utilisateurs CHANTIX** (1 pilote actif, 2 signups à activer), **MRR 0 €** — 9 places pilotes libres. RHÉA a repéré 1 abonné inactif depuis 12 jours à réactiver. Je dispatche par priorité ?",
    pills: ["Vue d'ensemble", "Snapshot business", "Dispatch", "Priorisation"],
  },
  chanti: {
    user: "Thierry, quelles sont les priorités produit CHANTIX cette semaine ?",
    agent: "J'ai croisé les 12 derniers feedbacks : **2 patterns forts**. (1) 3 users sur 5 se plaignent du temps de saisie initial d'un chantier → impact HAUT, effort M. (2) La feature 'export PDF' est demandée 4 fois → impact M, effort S. Recommandation : shipper l'export PDF en 1er (quick win), puis attaquer l'onboarding. Tu valides ?",
    pills: ["Analyse feedback", "Roadmap", "Priorisation", "Specs"],
  },
  casper: {
    user: "Casper, il me faut des CGV SaaS pour CHANTIX.",
    agent: "Compris. Je rédige des CGV conformes 2026 : objet, prix & modalités de paiement, essai 14 j, résiliation, RGPD (art. 28 sous-traitance), garanties, propriété intellectuelle, loi applicable. **Version SaaS pour EI micro-entreprise** — je te la sors en 30 secondes, tu valides avant publication sur le site.",
    pills: ["CGU/CGV SaaS", "RGPD", "e-invoicing 2026", "Contrats"],
  },
  clara: {
    user: "Clara, lance la campagne LinkedIn pour recruter 9 pilotes CHANTIX.",
    agent: "OK. Je génère un post storytelling autour de ton 1er pilote (anonymisé), + une séquence de 47 messages personnalisés pour des OPC/CSPS ciblés Nouvelle-Aquitaine. Tu valides les 5 meilleurs demain matin dans **Prospection IA**. En bonus : la séquence J0/J3/J7/J30 est prête pour tout nouveau signup.",
    pills: ["Contenu", "Prospection", "Onboarding", "Relances"],
  },
  fisco: {
    user: "Ulrich, où en est mon MRR ?",
    agent: "**MRR actuel : 0 €** (0 abonnement actif) · **1 pilote en essai gratuit** (Thierry Rivière — se termine dans 8 jours). Dès qu'il bascule payant à 29 €/mois, ton MRR passe à 29 €. Recommandation : programme un check-in J-2 avant la fin d'essai pour éviter le churn silencieux.",
    pills: ["MRR & ARR", "Factures conformes", "Dunning", "Encaissements"],
  },
  rhea: {
    user: "Rhéa, qui n'est plus actif chez mes users CHANTIX ?",
    agent: "**1 user à risque élevé** : Thierry Rivière Castellanos (pilote OPC) — dernière activité il y a 12 jours, 0 chantiers créés depuis l'inscription. Je propose un email de check-in personnalisé + un appel Voxy en soutien. En parallèle, les 2 signups non-pilotes n'ont jamais activé leur compte → séquence relance prête.",
    pills: ["Onboarding", "Activation", "Détection churn", "Réactivation"],
  },
  opera: {
    user: "Opéra, où en sont mes tickets support cette semaine ?",
    agent: "**0 ticket ouvert** (pile vide 🎯). Sur les 7 derniers jours : 0 incident, 0 escalade produit à Thierry. Reco : profite de ce calme pour finaliser le **playbook 'onboarding pilote'** — je te le prépare en 5 min si tu veux, il te servira à tenir la charge quand tu passeras à 10 pilotes.",
    pills: ["Tickets", "Incidents", "Playbooks", "SLA"],
  },
  visio: {
    user: "Visio, snapshot exécutif de la semaine.",
    agent: "📊 **3 users** (+0 vs sem. dernière) · **1 pilote actif** · **MRR 0 €** · **Conversion visiteur → signup ~ ND** (pas de tracking analytics encore). ⚠️ **Anomalie** : ton pilote n'a créé aucun chantier depuis 12 jours — signal churn probable. Reco : activer Google Analytics + un check-in Rhéa cette semaine.",
    pills: ["KPIs business", "MRR & conversion", "Anomalies", "Reporting"],
  },
  voxy: {
    user: "Voxy, prépare le script d'appel pour un nouveau signup.",
    agent: "« Bonjour, ici Voxy pour GABRYX. Vous venez de créer votre compte CHANTIX, félicitations ! J'ai 90 secondes pour comprendre votre besoin et vous connecter avec Gabriel ou vous envoyer un guide personnalisé. Vous êtes CSPS, OPC ou conducteur ? » — Prêt à être branché sur Twilio dès que tu le décides.",
    pills: ["Appels sortants", "Qualification", "Onboarding vocal", "Scripts"],
  },
};


const COVERAGE_LABEL = {
  live: { label: "Live CHANTIX", color: "#10B981" },
  hybrid: { label: "Hybride", color: "#F59E0B" },
  advisory: { label: "Conseil expert", color: "#A1A1AA" },
};


export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents");
      setAgents(data.active || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-8 lg:py-12">
        {/* Hero */}
        <div className="mb-10 lg:mb-14" data-testid="agents-hero">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Votre équipe IA</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] max-w-3xl">
            Une équipe d&apos;agents IA <span className="text-amber-400">autonomes</span> au service de GABRYX.
          </h1>
          <p className="text-zinc-400 mt-5 max-w-2xl text-sm lg:text-base leading-relaxed">
            Disponibles 24h/24, 7j/7. Chacun avec son expertise, sa personnalité, ses outils.
            Tu leur parles comme à un collaborateur — ils exécutent. Tu valides — ils envoient.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <Link
              to="/app/mission"
              data-testid="agents-cta-mission"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-amber-400 text-black hover:bg-amber-300 transition font-medium"
            >
              <Sparkles className="w-4 h-4" />
              Voir Mission Control
            </Link>
            <Link
              to="/app/chat/gaby"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-white/15 hover:bg-white/5 transition"
            >
              <MessageSquare className="w-4 h-4" />
              Parler à GABY
            </Link>
            <button
              onClick={refresh}
              className="ml-auto p-2 text-zinc-500 hover:text-white transition"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Agents showcase */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-16 lg:space-y-24">
            {agents.map((agent, idx) => (
              <AgentShowcase key={agent.code} agent={agent} reverse={idx % 2 === 1} />
            ))}
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-16 lg:mt-24 border border-white/10 p-8 lg:p-12 text-center bg-gradient-to-br from-amber-500/[0.05] to-transparent">
          <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mb-3">
            10 agents. 1 cockpit. Zéro micro-management.
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6 text-sm leading-relaxed">
            Lance Mission Control. Tes agents préparent les actions du jour à 06:30,
            tu valides en 5 minutes, ils exécutent. C&apos;est tout.
          </p>
          <Link
            to="/app/mission"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-white text-black hover:bg-zinc-200 transition font-medium"
          >
            Lancer Mission Control
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </ConsoleShell>
  );
}


function AgentShowcase({ agent, reverse }) {
  const v = AGENT_VISUALS[agent.code] || {};
  const demo = DEMOS[agent.code] || {
    user: "Bonjour, comment peux-tu m'aider ?",
    agent: "Je suis spécialisé dans " + agent.role.toLowerCase() + ". Demande-moi ce dont tu as besoin.",
    pills: [],
  };
  const cov = COVERAGE_LABEL[v.coverage] || COVERAGE_LABEL.advisory;

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
      data-testid={`agent-showcase-${agent.code}`}
    >
      {/* Conversation side */}
      <div className="space-y-3">
        {/* User bubble */}
        <div className="flex justify-end">
          <div className="max-w-[85%] bg-white text-black px-4 py-2.5 text-sm leading-relaxed shadow-lg rounded-[2px]">
            {demo.user}
          </div>
        </div>

        {/* Agent typing label + bubble */}
        <div className="flex items-end gap-2">
          <img
            src={v.avatar}
            alt={agent.name}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            style={{ boxShadow: `0 0 0 2px ${v.color}` }}
          />
          <div
            className="max-w-[85%] px-4 py-2.5 text-sm leading-relaxed shadow-lg rounded-[2px]"
            style={{
              backgroundColor: v.bgSoft,
              border: `1px solid ${v.color}30`,
              color: "#fafafa",
            }}
            dangerouslySetInnerHTML={{
              __html: demo.agent.replace(
                /\*\*(.+?)\*\*/g,
                `<strong style="color:${v.color}">$1</strong>`,
              ),
            }}
          />
        </div>

        {/* Capability pills */}
        {demo.pills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-11 pt-2">
            {demo.pills.map((p) => (
              <span
                key={p}
                className="text-[10px] uppercase tracking-wider px-2 py-1 border"
                style={{
                  borderColor: `${v.color}30`,
                  color: v.color,
                }}
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Agent profile side */}
      <div className="lg:pl-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: cov.color }}
          />
          <span style={{ color: cov.color }}>{cov.label}</span>
          <span>·</span>
          <span>{agent.code}</span>
        </div>

        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-zinc-500 text-sm">Découvrez</span>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">
            {agent.name}
          </h2>
        </div>

        <p className="text-zinc-400 leading-relaxed mb-5 max-w-md text-sm lg:text-[15px]">
          {v.description || agent.role}
        </p>

        {/* Capabilities list */}
        {v.skills && v.skills.length > 0 && (
          <ul className="space-y-2 mb-6">
            {v.skills.map((s) => (
              <li key={s} className="flex items-start gap-2 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: v.color }} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/app/chat/${agent.code}`}
            data-testid={`agent-chat-cta-${agent.code}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm transition font-medium"
            style={{
              backgroundColor: v.color,
              color: "#0a0a0a",
            }}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Parler à {agent.name}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to={`/app/agents/${agent.code}`}
            className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"
          >
            Voir plus de détails →
          </Link>
        </div>
      </div>
    </div>
  );
}
